<template>
  <div>
    <v-menu
      v-model="showMenuFilter"
      :close-on-content-click="false"
      :nudge-width="200"
      offset-y
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          class="menu-button menu-button-filter"
          :class="{
            'active-filters': hasActiveFilters,
          }"
          color="white accent-4"
          small
          elevation="1"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon>mdi-filter-outline</v-icon>
          Filtros
        </v-btn>
      </template>

      <v-card class="filter-card">
        <v-list>
          <v-list-item>
            <v-menu
              v-model="menuFromDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="fromDateFormatted"
                  slot="activator"
                  @click:clear="handleCleanFilter('fromDate')"
                  v-on="on"
                  label="Data Inicial"
                  placeholder="Escolha uma data inicial..."
                  color="graydark"
                  outlined
                  hide-details
                  clearable
                  autocomplete="new-password"
                  prepend-inner-icon="today"
                  append-icon="mdi-menu-down"
                  readonly
                />
              </template>

              <v-date-picker
                v-model="model.fromDate"
                show-current
                no-title
                scrollable
                color="primary"
                locale="pt-br"
                :max="maxFromDateValue"
                @click:date="validateDates"
                @input="menuFromDate = false"
              />
            </v-menu>
          </v-list-item>

          <v-list-item>
            <v-menu
              v-model="menuToDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="toDateFormatted"
                  slot="activator"
                  @click:clear="handleCleanFilter('toDate')"
                  v-on="on"
                  label="Data Final"
                  placeholder="Escolha uma data final..."
                  color="graydark"
                  outlined
                  hide-details
                  clearable
                  autocomplete="new-password"
                  prepend-inner-icon="event"
                  append-icon="mdi-menu-down"
                  readonly
                />
              </template>

              <v-date-picker
                v-model="model.toDate"
                show-current
                no-title
                scrollable
                color="primary"
                locale="pt-br"
                :min="minToDateValue"
                :max="maxToDateValue"
                @click:date="validateDates()"
                @input="menuToDate = false"
              />
            </v-menu>
          </v-list-item>

          <v-list-item>
            <v-text-field
              v-model="model.author"
              @click:clear="author = undefined"
              label="Autor"
              placeholder="Adicione o autor..."
              color="graydark"
              outlined
              hide-details
              clearable
              autocomplete="new-password"
              prepend-inner-icon="person"
            />
          </v-list-item>
        </v-list>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            outlined
            class="button-apply"
            @click="handleFilter"
          >
            Aplicar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <v-btn
      v-if="hasActiveFilters"
      class="menu-button-clear"
      color="white accent-4"
      small
      elevation="0"
      @click="handleCleanAllFilter"
    >
      <v-icon small class="mr-0">mdi-close</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { dateFormat, isoDateFormat } from '@/utils/date.js';
import { sortOrder } from '@/utils/mappers.js';
import makeDeepCopy from '@/utils/makeDeepCopy.js';
import { debounce } from 'lodash';

export default {
  name: 'HomeSearchMenuFilter',
  props: {
    value: {
      type: Object,
      default: () => ({
        fromDate: undefined,
        toDate: undefined,
        author: undefined,
      }),
    },
  },

  data() {
    return {
      sortOrder,
      showMenuFilter: false,
      menuFromDate: null,
      menuToDate: null,
      maxFromDateValue: undefined,
      maxToDateValue: undefined,
      minToDateValue: undefined,
      model: {
        order: sortOrder.DESC,
        fromDate: undefined,
        toDate: undefined,
        author: undefined,
      },
    };
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler() {
        this.sync();
      },
    },
  },

  computed: {
    hasActiveFilters() {
      return (
        !!this.model.fromDate || !!this.model.toDate || !!this.model.author
      );
    },

    maxDateValue() {
      return isoDateFormat(new Date());
    },

    fromDateFormatted: {
      get() {
        return this.model.fromDate
          ? dateFormat(this.model.fromDate)
          : undefined;
      },
      set() {},
    },
    toDateFormatted: {
      get() {
        return this.model.toDate ? dateFormat(this.model.toDate) : undefined;
      },
      set() {},
    },
  },

  methods: {
    sync() {
      this.model = makeDeepCopy(this.value);
      this.validateDates();
    },
    validateDates() {
      // 1. make sure fromDate is always smaller or equal toDate
      // 2. make sure toDate is not bigger than today
      this.minToDateValue = this.model.fromDate
        ? this.model.fromDate
        : undefined;
      this.maxFromDateValue = this.model.toDate
        ? this.model.toDate
        : this.maxDateValue;
    },
    handleCleanAllFilter() {
      this.model = {
        order: this.model.order,
        fromDate: undefined,
        toDate: undefined,
        author: undefined,
      };
      this.handleChangeFilter();
    },
    handleCleanFilter(field) {
      this.model[field] = undefined;
      this.handleChangeFilter();
    },
    handleChangeFilter() {
      this.handleFilter();
      this.validateDates();
    },
    handleFilter() {
      this.showMenuFilter = false;
      this.$emit('update-filters', makeDeepCopy(this.model));
      this.applyFilters();
    },
    applyFilters: debounce(function() {
      if (!(this.menuFromDate || this.menuToDate)) {
        this.$emit('apply-filters');
        this.$emit('has-active-filters', this.hasActiveFilters);
      }
    }, parseInt(process.env.VUE_APP_FILTER_DELAY) || 2000),
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/theme-v2.scss';
@import '@/assets/scss/buttons.scss';
@import '@/assets/scss/inputs.scss';

.menu-button {
  color: $color-black-64;
  font-weight: 500 !important;
  text-transform: initial;
}
.menu-button-filter {
  &.active-filters {
    border-radius: 5px 0 0 5px;
  }
}
.menu-button-clear {
  padding: 0 !important;
  border-radius: 0 5px 5px 0;
  border-left: 1px solid $color-black-16 !important;
}

.v-list,
.filter-card {
  background-color: $color-background;
}
.v-list {
  .v-list-item {
    padding: 15px;
  }
}

.button-apply {
  background-color: $color-white;
  text-transform: inherit;
}
</style>
